import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getSupabase } from '../../lib/supabase-lazy';
import { colors } from '../../lib/colors';

interface ProjectSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  onSave: (project: Partial<Project>) => void;
}

interface Project {
  id: string;
  name: string;
  description: string | null;
  status: 'active' | 'completed' | 'on_hold' | 'cancelled';
  client_id: string | null;
  start_date: string | null;
  end_date: string | null;
}

interface Party {
  id: string;
  name: string;
  given_name: string;
  family_name: string;
  party_type: 'individual' | 'organization';
}

export default function ProjectSettingsModal({ isOpen, onClose, project, onSave }: ProjectSettingsModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active' as 'active' | 'completed' | 'on_hold' | 'cancelled',
    client_id: '',
    start_date: '',
    end_date: '',
  });
  const [clients, setClients] = useState<Party[]>([]);
  const [supabase, setSupabase] = useState<any>(null);

  useEffect(() => {
    getSupabase().then(setSupabase);
  }, []);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        description: project.description || '',
        status: project.status,
        client_id: project.client_id || '',
        start_date: project.start_date ? project.start_date.split('T')[0] : '',
        end_date: project.end_date ? project.end_date.split('T')[0] : '',
      });
    }
  }, [project]);

  useEffect(() => {
    if (supabase) {
      fetchClients();
    }
  }, [supabase]);

  const fetchClients = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('parties')
        .select('id, name, given_name, family_name, party_type')
        .order('name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching clients:', error);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const updatedProject: Partial<Project> = {
      name: formData.name,
      description: formData.description || null,
      status: formData.status,
      client_id: formData.client_id || null,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
    };

    onSave(updatedProject);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel 
          className="mx-auto max-w-2xl w-full rounded-xl shadow-2xl border border-white/10"
          style={{ backgroundColor: colors.premium.dark }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <Dialog.Title className="text-lg font-semibold text-white">
              Project Settings
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors rounded-lg p-1 hover:bg-white/10"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              {/* Project Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300">
                  Project Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-lg bg-white/10 border-white/10 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 sm:text-sm text-white placeholder-gray-500 backdrop-blur-sm"
                  placeholder="Enter project name"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 block w-full rounded-lg bg-white/10 border-white/10 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 sm:text-sm text-white placeholder-gray-500 backdrop-blur-sm"
                  placeholder="Describe the project objectives"
                />
              </div>

              {/* Status and Client */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="mt-1 block w-full rounded-lg bg-white/10 border-white/10 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 sm:text-sm text-white backdrop-blur-sm"
                  >
                    <option value="active" className="bg-gray-800">Active</option>
                    <option value="on_hold" className="bg-gray-800">On Hold</option>
                    <option value="completed" className="bg-gray-800">Completed</option>
                    <option value="cancelled" className="bg-gray-800">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    Client
                  </label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="mt-1 block w-full rounded-lg bg-white/10 border-white/10 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 sm:text-sm text-white backdrop-blur-sm"
                  >
                    <option value="" className="bg-gray-800">No Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id} className="bg-gray-800">
                        {client.party_type === 'organization' 
                          ? client.name 
                          : `${client.given_name} ${client.family_name}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Start and End Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-1 block w-full rounded-lg bg-white/10 border-white/10 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 sm:text-sm text-white backdrop-blur-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    min={formData.start_date}
                    className="mt-1 block w-full rounded-lg bg-white/10 border-white/10 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 sm:text-sm text-white backdrop-blur-sm"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 hover:text-white transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white rounded-lg shadow-lg transition-all duration-200 hover:scale-105"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                  boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`
                }}
              >
                Save Changes
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}